import { Helmet } from 'react-helmet-async';

export default function TerminosPage() {
  return (
    <>
      <Helmet>
        <title>Términos y Condiciones del Servicio | EhostingChile</title>
        <meta name="description" content="Términos y condiciones de uso de los servicios de hosting, VPS y servidores de EhostingChile. Políticas de uso aceptable y condiciones contractuales." />
        <link rel="canonical" href="https://www.ehostingchile.com/terminos" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-cyan-500/5 to-teal-500/5"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white mb-6 shadow-lg shadow-blue-500/30">
                <i className="ri-file-text-line text-4xl"></i>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Términos de Servicio
              </h1>
              <p className="text-xl text-gray-600">
                Última actualización: Enero 2025
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-3xl p-10 md:p-16 shadow-xl border border-gray-100">
              
              {/* Introducción */}
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Los presentes Términos de Servicio (en adelante, "Términos") regulan la relación contractual entre <strong>EhostingChile.com</strong> (en adelante, "EhostingChile", "nosotros" o "la Empresa") y el cliente (en adelante, "Cliente" o "usted") respecto a la contratación y uso de servicios de hosting web, servidores VPS, registro de dominios, correo electrónico y servicios relacionados.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Al contratar cualquiera de nuestros servicios, el Cliente acepta expresamente estos Términos en su totalidad. Si no está de acuerdo con alguna cláusula, le solicitamos no contratar nuestros servicios.
                </p>
              </div>

              {/* 1. Objeto del Contrato */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 text-xl">1</span>
                  Objeto del Contrato
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    EhostingChile se compromete a proveer al Cliente los servicios de hosting web, servidores virtuales privados (VPS), registro y gestión de dominios, correo electrónico corporativo y servicios complementarios contratados, de acuerdo con las especificaciones técnicas y características publicadas en nuestro sitio web <a href="https://www.ehostingchile.com" className="text-blue-600 hover:underline">www.ehostingchile.com</a>.
                  </p>
                  <p>
                    Los servicios se prestarán bajo las condiciones técnicas, comerciales y de soporte establecidas en el plan contratado, y estarán sujetos a disponibilidad de recursos y cumplimiento de las políticas de uso aceptable.
                  </p>
                </div>
              </div>

              {/* 2. Condiciones de Uso */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 text-xl">2</span>
                  Condiciones de Uso y Política de Uso Aceptable
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p className="font-semibold text-gray-900">El Cliente se compromete a:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Utilizar los servicios únicamente para fines legales y legítimos, cumpliendo con todas las leyes aplicables en Chile y en las jurisdicciones donde opere.</li>
                    <li>No alojar, transmitir o distribuir contenido ilegal, difamatorio, obsceno, fraudulento, que infrinja derechos de propiedad intelectual o que viole derechos de terceros.</li>
                    <li>No utilizar los servicios para envío masivo de correos no solicitados (spam), phishing, distribución de malware, ataques informáticos o cualquier actividad que comprometa la seguridad de la red.</li>
                    <li>No realizar actividades que consuman recursos excesivos del servidor afectando el rendimiento de otros clientes (uso abusivo de CPU, RAM, ancho de banda o procesos).</li>
                    <li>Mantener actualizadas las aplicaciones, scripts y sistemas instalados en su cuenta para prevenir vulnerabilidades de seguridad.</li>
                    <li>Realizar copias de seguridad periódicas de su información. EhostingChile realiza respaldos automáticos, pero el Cliente es responsable final de sus datos.</li>
                  </ul>
                  <p className="font-semibold text-gray-900 mt-6">Actividades Prohibidas:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Hosting de contenido pornográfico infantil, apología del terrorismo, tráfico de drogas o cualquier actividad ilícita.</li>
                    <li>Sitios de phishing, warez, cracks, keygens o distribución de software pirata.</li>
                    <li>Minería de criptomonedas o uso de recursos para procesamiento intensivo no autorizado.</li>
                    <li>Proxy público, TOR exit nodes o servicios que permitan anonimización masiva sin autorización previa.</li>
                    <li>Almacenamiento de archivos para descarga masiva (file hosting) sin plan específico para ello.</li>
                  </ul>
                  <p className="mt-4">
                    <strong>Consecuencias del incumplimiento:</strong> EhostingChile se reserva el derecho de suspender o cancelar inmediatamente cualquier servicio que viole estas políticas, sin previo aviso y sin derecho a reembolso.
                  </p>
                </div>
              </div>

              {/* 3. Planes, Precios y Pagos */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-xl">3</span>
                  Planes, Precios y Formas de Pago
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Los precios de nuestros servicios están publicados en pesos chilenos (CLP) y no incluyen IVA (19%), el cual se agregará al momento de la facturación según la legislación tributaria vigente en Chile.
                  </p>
                  <p className="font-semibold text-gray-900">Formas de pago aceptadas:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Transferencia bancaria (bancos chilenos)</li>
                    <li>WebPay (tarjetas de crédito y débito chilenas)</li>
                    <li>PayPal (pagos internacionales)</li>
                    <li>Tarjetas de crédito internacionales (Visa, Mastercard, American Express)</li>
                  </ul>
                  <p>
                    Los servicios se activan una vez confirmado el pago. Para transferencias bancarias, la activación puede demorar hasta 24 horas hábiles mientras se verifica el depósito.
                  </p>
                  <p>
                    <strong>Cambios de precio:</strong> EhostingChile se reserva el derecho de modificar los precios de sus servicios con un aviso previo de 30 días. Los cambios no afectarán los períodos ya pagados, pero se aplicarán en la siguiente renovación.
                  </p>
                  <p>
                    <strong>Impuestos:</strong> El Cliente es responsable del pago de todos los impuestos aplicables según su jurisdicción fiscal.
                  </p>
                </div>
              </div>

              {/* 4. Renovaciones */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600 text-xl">4</span>
                  Renovaciones y Vencimientos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Los servicios de hosting, VPS y dominios se contratan por períodos determinados (mensual, trimestral, semestral o anual) y deben renovarse antes de su fecha de vencimiento para mantener la continuidad del servicio.
                  </p>
                  <p>
                    <strong>Notificaciones de renovación:</strong> EhostingChile enviará recordatorios de renovación por correo electrónico con 30, 15 y 7 días de anticipación al vencimiento. Es responsabilidad del Cliente mantener actualizada su dirección de correo electrónico.
                  </p>
                  <p>
                    <strong>Renovación automática:</strong> Si el Cliente activa la renovación automática, el servicio se renovará automáticamente usando el método de pago registrado. El Cliente puede desactivar esta opción en cualquier momento desde su panel de control.
                  </p>
                  <p>
                    <strong>Período de gracia:</strong> Los servicios de hosting y VPS tienen un período de gracia de 7 días después del vencimiento, durante el cual el servicio permanece activo pero con acceso limitado. Si no se renueva en este período, el servicio será suspendido.
                  </p>
                  <p>
                    <strong>Suspensión por falta de pago:</strong> Después del período de gracia, el servicio será suspendido y los datos permanecerán almacenados por 15 días adicionales. Pasado este tiempo, los datos serán eliminados permanentemente sin posibilidad de recuperación.
                  </p>
                  <p>
                    <strong>Dominios:</strong> Los dominios tienen políticas específicas según su extensión. Los dominios .cl tienen un período de gracia de 30 días. Dominios internacionales (.com, .net, etc.) tienen 30 días de gracia más 30 días de período de redención con costo adicional de recuperación.
                  </p>
                </div>
              </div>

              {/* 5. Cancelaciones y Reembolsos */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 text-xl">5</span>
                  Cancelaciones y Política de Reembolso
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>Garantía de satisfacción de 30 días:</strong> Los planes de hosting web (compartido) cuentan con una garantía de devolución de dinero de 30 días desde la fecha de contratación inicial. Si no está satisfecho con el servicio, puede solicitar un reembolso completo dentro de este período.
                  </p>
                  <p className="font-semibold text-gray-900">Exclusiones de la garantía:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Servicios VPS, servidores dedicados y dominios no están cubiertos por la garantía de reembolso.</li>
                    <li>Renovaciones de servicios (solo aplica a contrataciones nuevas).</li>
                    <li>Servicios adicionales como certificados SSL de pago, licencias de software, etc.</li>
                    <li>Cuentas suspendidas por violación de términos de servicio.</li>
                  </ul>
                  <p>
                    <strong>Proceso de cancelación:</strong> El Cliente puede cancelar su servicio en cualquier momento desde su panel de control o contactando a soporte. La cancelación será efectiva al final del período pagado, sin reembolso prorrateado (excepto dentro del período de garantía de 30 días).
                  </p>
                  <p>
                    <strong>Responsabilidad de respaldo:</strong> Antes de cancelar, el Cliente debe descargar todos sus archivos, bases de datos y correos electrónicos. EhostingChile no es responsable de la pérdida de datos después de la cancelación.
                  </p>
                  <p>
                    <strong>Tiempo de procesamiento:</strong> Los reembolsos aprobados se procesarán en un plazo de 7 a 15 días hábiles, dependiendo del método de pago original.
                  </p>
                </div>
              </div>

              {/* 6. SLA y Garantía de Uptime */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50 text-teal-600 text-xl">6</span>
                  Acuerdo de Nivel de Servicio (SLA) - Garantía 99.9%
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    EhostingChile garantiza un <strong>99.9% de uptime</strong> (tiempo de actividad) mensual para servicios de hosting web y VPS, medido desde nuestros sistemas de monitoreo interno.
                  </p>
                  <p className="font-semibold text-gray-900">Exclusiones del SLA:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Mantenimientos programados notificados con al menos 48 horas de anticipación.</li>
                    <li>Interrupciones causadas por el Cliente (errores en scripts, consumo excesivo de recursos, configuraciones incorrectas).</li>
                    <li>Ataques DDoS o eventos de fuerza mayor fuera del control de EhostingChile.</li>
                    <li>Problemas de conectividad del lado del Cliente o su ISP.</li>
                    <li>Suspensiones por violación de términos de servicio o falta de pago.</li>
                  </ul>
                  <p>
                    <strong>Compensación por incumplimiento:</strong> Si el uptime mensual cae por debajo del 99.9% por causas atribuibles a EhostingChile, el Cliente podrá solicitar un crédito proporcional en su cuenta según la siguiente tabla:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 my-4">
                    <ul className="space-y-2">
                      <li><strong>99.0% - 99.8% uptime:</strong> 10% de crédito del pago mensual</li>
                      <li><strong>98.0% - 98.9% uptime:</strong> 25% de crédito del pago mensual</li>
                      <li><strong>Menos de 98.0% uptime:</strong> 50% de crédito del pago mensual</li>
                    </ul>
                  </div>
                  <p>
                    El crédito debe solicitarse dentro de los 7 días siguientes al mes afectado, presentando evidencia del tiempo de inactividad. El crédito se aplicará como descuento en la siguiente factura y no es reembolsable en efectivo.
                  </p>
                </div>
              </div>

              {/* 7. Limitaciones de Responsabilidad */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 text-xl">7</span>
                  Limitaciones de Responsabilidad
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    EhostingChile proporciona sus servicios "tal cual" y no garantiza que estarán libres de errores o interrupciones. Nuestra responsabilidad se limita al monto pagado por el Cliente en los últimos 12 meses por el servicio específico afectado.
                  </p>
                  <p className="font-semibold text-gray-900">EhostingChile no será responsable por:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Pérdida de datos, ingresos, beneficios o daños indirectos, incluso si se advirtió de su posibilidad.</li>
                    <li>Contenido publicado por el Cliente o sus usuarios finales.</li>
                    <li>Violaciones de seguridad causadas por vulnerabilidades en aplicaciones del Cliente (WordPress, Joomla, scripts personalizados, etc.).</li>
                    <li>Incompatibilidad de software o aplicaciones de terceros con nuestros servidores.</li>
                    <li>Acciones de autoridades gubernamentales, órdenes judiciales o cumplimiento de leyes aplicables.</li>
                    <li>Ataques informáticos, virus, malware o acciones maliciosas de terceros.</li>
                  </ul>
                  <p>
                    <strong>Respaldos:</strong> Aunque EhostingChile realiza respaldos automáticos diarios, estos son una cortesía y no sustituyen la obligación del Cliente de mantener sus propias copias de seguridad. No garantizamos la disponibilidad o integridad de los respaldos en todos los casos.
                  </p>
                  <p>
                    <strong>Migración de datos:</strong> Ofrecemos servicio de migración gratuita como cortesía, pero no nos hacemos responsables por pérdida de datos, incompatibilidades o problemas derivados del proceso de migración.
                  </p>
                </div>
              </div>

              {/* 8. Propiedad Intelectual */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-pink-50 text-pink-600 text-xl">8</span>
                  Propiedad Intelectual
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Todos los derechos de propiedad intelectual sobre el sitio web de EhostingChile, su contenido, diseño, logotipos, marcas comerciales, software propietario y documentación son propiedad exclusiva de EhostingChile o sus licenciantes.
                  </p>
                  <p>
                    El Cliente conserva todos los derechos sobre el contenido que aloja en nuestros servidores (textos, imágenes, videos, bases de datos, etc.). Al utilizar nuestros servicios, el Cliente otorga a EhostingChile una licencia limitada para almacenar, copiar y transmitir dicho contenido únicamente con el propósito de prestar el servicio contratado.
                  </p>
                  <p>
                    El Cliente garantiza que tiene todos los derechos necesarios sobre el contenido que aloja y que no infringe derechos de propiedad intelectual de terceros. EhostingChile responderá a notificaciones de infracción de derechos de autor conforme a la legislación chilena aplicable.
                  </p>
                </div>
              </div>

              {/* 9. Modificaciones */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-yellow-50 text-yellow-600 text-xl">9</span>
                  Modificaciones a los Términos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    EhostingChile se reserva el derecho de modificar estos Términos de Servicio en cualquier momento. Las modificaciones entrarán en vigor 30 días después de su publicación en nuestro sitio web.
                  </p>
                  <p>
                    Notificaremos a los Clientes activos sobre cambios significativos mediante correo electrónico. El uso continuado de los servicios después de la entrada en vigor de las modificaciones constituye la aceptación de los nuevos términos.
                  </p>
                  <p>
                    Si el Cliente no está de acuerdo con las modificaciones, puede cancelar su servicio antes de que entren en vigor y solicitar un reembolso prorrateado del tiempo no utilizado.
                  </p>
                </div>
              </div>

              {/* 10. Ley Aplicable */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 text-xl">10</span>
                  Ley Aplicable y Jurisdicción
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Estos Términos de Servicio se rigen por las leyes de la República de Chile. Cualquier controversia derivada de la interpretación o ejecución de estos términos será sometida a la jurisdicción de los tribunales ordinarios de justicia de Santiago de Chile, con renuncia expresa a cualquier otro fuero que pudiera corresponder.
                  </p>
                  <p>
                    Para efectos de notificaciones judiciales y extrajudiciales, EhostingChile fija domicilio en Av. Apoquindo 6410, of 202, Las Condes, Santiago, Chile.
                  </p>
                </div>
              </div>

              {/* 11. Contacto */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 text-xl">11</span>
                  Información de Contacto
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Para consultas sobre estos Términos de Servicio o cualquier aspecto de nuestros servicios, puede contactarnos a través de:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                    <p><strong>Correo electrónico:</strong> soporte@ehostingchile.com</p>
                    <p><strong>Teléfono:</strong> +56 2 2581 9100</p>
                    <p><strong>WhatsApp:</strong> +56 9 4247 0111</p>
                    <p><strong>Dirección:</strong> Av. Apoquindo 6410, of 202, Las Condes, Santiago, Chile</p>
                    <p><strong>Horario de atención:</strong> Lunes a Viernes 9:00 - 18:00 hrs (Soporte técnico 24/7)</p>
                  </div>
                </div>
              </div>

              {/* Footer del documento */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  Última actualización: Enero 2025 · EhostingChile.com · Todos los derechos reservados
                </p>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
}